import { createWorld, trait } from 'koota';

// ---------------------------------------------------------------------------
// Character traits — what a squirrel in the world looks like.
// ---------------------------------------------------------------------------

export const Pose = trait({ segmentId: '', s: 0, theta: 0, r: 0 });
export const CharacterName = trait({ value: '' });
export const CharacterIdentity = trait({ value: '' });
export const CharacterUserId = trait({ value: '' });
export const IsLocal = trait();
export const IsCharacter = trait();

// ---------------------------------------------------------------------------
// Segment traits — world geometry.
//
// Per the forest-hierarchy spec: segments are bezier-axis ready
// (v0 emits degenerate beziers; non-degenerate curves land later).
// Topology lives via parentSegmentId — children are derivable by
// querying segments whose parentSegmentId matches.
// ---------------------------------------------------------------------------

export const SegmentGeometry = trait({
  p0X: 0, p0Y: 0, p0Z: 0,
  p1X: 0, p1Y: 0, p1Z: 0,
  p2X: 0, p2Y: 0, p2Z: 0,
  p3X: 0, p3Y: 1, p3Z: 0,
  length: 0,
  radiusBase: 0,
  radiusTip: 0,
});
export const SegmentLinks = trait({
  parentSegmentId: '',
  treeId: '',
  regionId: '',
});
export const SegmentId = trait({ value: '' });
export const IsSegment = trait();

// ---------------------------------------------------------------------------
// Session traits — game-state machine lives on a single Session entity.
// ---------------------------------------------------------------------------

export type SessionStateValue =
  | 'LANDING'
  | 'WELCOME'
  | 'IDENTITY_SELECT'
  | 'WELCOME_BACK'
  | 'CONNECTING'
  | 'CONNECTED_NO_USER'
  | 'PLAYING'
  | 'BANNED';

export const SessionState = trait<{ value: SessionStateValue }>({
  value: 'LANDING' as SessionStateValue,
});

// Mirror of the localStorage `activeTokenHash`; null = no active identity
// (the picker is required to choose one).
export const ActiveTokenHash = trait({ value: '' });

// ---------------------------------------------------------------------------
// Input + camera traits — shared mutable state that both input listeners
// (keyboard, wheel, touch) and consumers (InputController, FollowCamera) read
// via session.get() in useFrame. Reactive trait updates aren't needed here;
// the consumers poll once per frame.
// ---------------------------------------------------------------------------

// Camera pitch (radians above horizontal). Written by mouse-wheel and
// two-finger touch gesture; read by FollowCamera.
export const CameraPitch = trait({ value: 0 });

// Virtual joystick from single-finger touch drag. y = axial (climb), in
// [-1, 1]. The lateral (spiral) axis is raycast-driven and lives outside
// this trait (see InputController.touchScreenPos in App.tsx). `active`
// is true while a single-finger drag is in progress. Written by
// InputController's touch handler; read by its useFrame.
export const TouchJoystick = trait({ y: 0, active: false });

// Stable singleton tag for the session entity.
export const IsSession = trait();

export const world = createWorld();

// Default camera pitch (~48° above horizontal). Mirrored from
// PITCH_DEFAULT in App.tsx. Imported into the spawn so the trait
// initializes correctly on first read.
const DEFAULT_PITCH_RAD = (48 * Math.PI) / 180;

// Lazily create the singleton session entity on first import.
let sessionEntity = world.queryFirst(IsSession);
if (!sessionEntity) {
  sessionEntity = world.spawn(
    IsSession,
    SessionState({ value: 'LANDING' }),
    ActiveTokenHash({ value: '' }),
    CameraPitch({ value: DEFAULT_PITCH_RAD }),
    TouchJoystick({ y: 0, active: false })
  );
}
export const session = sessionEntity;
